import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ServiceService } from 'src/app/instant-analysis/services/service.service';
import { TimespanService } from 'src/app/instant-analysis/services/timespan/timespan.service';
import { Item, ScenegraphEvent } from 'vega';
import embed, { Mode, VisualizationSpec } from 'vega-embed';
import { RainCalendarSelectionType } from '../models/enums/rain-calendar-selection-type';
import { RainCalendar } from '../models/interfaces/rain-calendar';
import { RainCalendarItem } from '../models/interfaces/rain-calendar-item';
import { getIntensity, intensityColors, intensityTexts, rainMultipliers } from '../util/rain-intensity';

/**
 * Um Calendário onde cada dia será colorido de acordo com a intensidade do
 * acumulado de chuva de 24 horas no último registro atual do dia.
 * As cores são:
 * - verde: fraca
 * - amarelo: moderada
 * - laranja: forte
 * - vermelho: muito forte
 */
@Component({
  selector: 'app-rain-calendar',
  templateUrl: './rain-calendar.component.html',
  styleUrls: ['./rain-calendar.component.css']
})
export class RainCalendarComponent extends RainCalendar implements OnInit {

  chartContainerSelector: string = '#calendar-chart-container';

  /** Indica se o gráfico está buscando dados ou não */
  loading = false;

  initDate:any = '';
  minFinalDate:any = '';
  maxFinalDate:any = '';

  /** Formulário de intervalo de tempo. */
  dateIntervalForm: FormGroup;
  /**
  * Textos do tooltips do raincalendarbar.
  */
  rainCalendarTooltipText:string = `O modo de exibição de Chuva mostra o maior 
    valor acumulado de chuva por dia. 
    O modo de exibição de Ocorrências mostra a quantidade de ocorrências 
    na escala logarítmica por dia.`;

  /** Modo de seleção das datas. */
  @Input() selectionType: RainCalendarSelectionType;

  /** Evento de fechamento do gráfico pelo botão de x */
  @Output() closed = new EventEmitter<boolean>();

  /** Evento de click em um item do gráfico. */
  @Output() itemClicked = new EventEmitter<RainCalendarItem>();

  @Output() dragDisabled = new EventEmitter<boolean>();

  /**
   * Construtor.
   * @param fb usado para criar o objeto do form group.
   */
  constructor(fb: FormBuilder,
    service: ServiceService,
    snackBar: MatSnackBar,
    timespanService: TimespanService,
  ) {
    super(fb, service, snackBar, timespanService);
  }

  /** Inicializa o formulário do intervalo de datas. */
  ngOnInit(): void {
    this.initForm();

    //inicializa tempo para data inicial
    this.initDate = new Date();
    this.initDate.setFullYear(this.initDate.getFullYear() - 1);
    this.initDate.setDate(this.initDate.getDate() + 1);
    this.initDate.setMonth(this.initDate.getMonth() + 1);
    let initMonth = this.initDate.getMonth();
    (initMonth < 10) ? initMonth = '0' + initMonth : initMonth = initMonth.toString();
    let initDate = this.initDate.getDate();
    (initDate < 10) ? initDate = '0' + initDate : initDate = initDate.toString();
    this.initDate = this.initDate.getFullYear() + '-' + initMonth + '-' + initDate;
    //inicializa data minima para ser sempre 1 dia a mais do que a data inicial
    this.minFinalDate = new Date();
    this.minFinalDate.setFullYear(this.minFinalDate.getFullYear() - 1);
    this.minFinalDate.setDate(this.minFinalDate.getDate() + 2);
    this.minFinalDate.setMonth(this.minFinalDate.getMonth() + 1);
    let minFinalMonth = this.minFinalDate.getMonth();
    (minFinalMonth < 10) ? minFinalMonth = '0' + minFinalMonth : minFinalMonth = minFinalMonth.toString();
    let minFinalDate = this.minFinalDate.getDate();
    (minFinalDate < 10) ? minFinalDate = '0' + minFinalDate : minFinalDate = minFinalDate.toString();
    this.minFinalDate = this.minFinalDate.getFullYear() + '-' + minFinalMonth + '-' + minFinalDate;
    //inicializa data maxima de 1 ano -1 dia a partir do dia inicial
    this.maxFinalDate = new Date();
    let maxFinalMonth = this.maxFinalDate.getMonth();
    (maxFinalMonth < 10) ? maxFinalMonth = '0' + maxFinalMonth : maxFinalMonth = maxFinalMonth.toString();
    let maxFinalDate = this.maxFinalDate.getDate();
    (maxFinalDate < 10) ? maxFinalDate = '0' + maxFinalDate : maxFinalDate = maxFinalDate.toString();
    this.maxFinalDate = this.maxFinalDate.getFullYear() + '-' + maxFinalMonth + '-' + maxFinalDate;
  }

  /**
   * Trata o click no botão de fechar o gráfico.
   */
  onCloseClicked() {
    this.closed.emit(true);
  }

  /**
   * Cria a especificação vega-lite do calendário de chuvas.
   * @param dataPoints dados dos dias com a maior medida do dia a serem exibidos no calendário.
   * @returns spec do vega-lite.
   */
  createRainChartSpec(dataPoints: any[]): VisualizationSpec {
    return {
      width: 'container',
      // height: 'container',
      background: 'transparent',
      data: {
        values: dataPoints,
      },
      layer: [
        {
          mark: 'rect',
          encoding: {
            x: {
              field: 'tempo',
              timeUnit: 'date',
              type: 'ordinal',
              title: 'Dia',
              
            },
            y: {
              field: 'tempo',
              timeUnit: 'month',
              type: 'ordinal',
              title: 'Mês',
            },
            color: {
              field: 'intensity',
              type: 'nominal',
              legend: {
                title: 'Chuva'
              },
              scale: {
                domain: intensityTexts,
                range: intensityColors,
              }
            },
            tooltip: [
              { field: 'tempo', type: 'temporal', title: 'Data', timeUnit: 'yearmonthdatehoursminutes' },
              { field: 'max_val', type: 'quantitative', title: 'Chuva' },
              { field: 'intensity', type: 'nominal', title: 'Intensidade' },
            ]
          },
        },
      ],
    };
  }

  /**
   * Cria a especificação vega-lite do calendário de ocorrências.
   * @param dataPoints dados dos dias com a contagem de ocorrências a serem exibidos no calendário.
   * @param eventType tipo da ocorrência (alagamento, deslizamento, todos).
   * @returns spec do vega-lite.
   */
  createEventChartSpec(dataPoints: any[],eventType: string): VisualizationSpec {
    return {
      width: 'container',
      // height: 'container',
      background: 'transparent',
      data: {
        values: dataPoints,
      },
      layer: [
        {
          mark: 'rect',
          encoding: {
            x: {
              field: 'tempo',
              timeUnit: 'date',
              type: 'ordinal',
              title: 'Dia',
              
            },
            y: {
              field: 'tempo',
              timeUnit: 'month',
              type: 'ordinal',
              title: 'Mês',
            },
            color: {
              field: eventType,
              type: 'quantitative',
              legend: {
                title: 'ocorrencia'
              },
              scale: {
                scheme: 'browns',
                // type:'log',
              }
            },
            tooltip: [
              { field: 'tempo', type: 'temporal', title: 'Data', timeUnit: 'yearmonthdatehoursminutes' },
              { field: 'todos', type: 'quantitative', title: 'ocorrencia' },
              { field: 'alagamento', type: 'quantitative', title: 'alagamento' },
              { field: 'deslizamento', type: 'quantitative', title: 'deslizamento' },
              // { field: 'intensity', type: 'nominal', title: 'Intensidade' },
            ]
          },
        },
      ],
    };
  }

  /**
   * Trata o clique em um item de dados no gráfico do calendário de datas.
   * @param event evento do click
   * @param item item de dados clicado
   * @returns 
   */
  onItemClicked(event: ScenegraphEvent, item: Item) {
    if (item == null || item.datum == null) {
      return;
    }

    let { aggregateCol } = this.dateIntervalForm.value;
    let val: RainCalendarItem = {
      dates: [
        new Date(item.datum.tempo),
      ],
      aggregateCol: aggregateCol,
    }
    this.itemClicked.emit(val);
  }

  /**
   * Emite o evento que indica se o componente pode ser arrastado ou não.
   * @param disabled indica se o drag n drop do componente está desabilidado ou não.
   */
  emitDragDisabled(disabled: boolean) {
    this.dragDisabled.emit(disabled);
  }

  /**
   * Trata a mudança no campo da data inicial do intervalo.
   * @param event evento da mudança no campo da data.
   */
  onInitialDateChange(event: any) {
    //OBS new Date(yyyy,mm,dd) -> o mes varia de 0-11 e nao de 1-12
    //pega ano, mes, dia do calendario quando muda uma data iniciall
    const [year, month, day] = event.target.value.split("-");

    //recalcula data minima para tempo final para 1 dia a mais que o dia inicial
    this.minFinalDate = new Date(year, month - 1, day);
    this.minFinalDate.setDate(this.minFinalDate.getDate() + 1);
    let minFinalMonth = this.minFinalDate.getMonth() + 1;
    (minFinalMonth < 10) ? minFinalMonth = '0' + minFinalMonth : minFinalMonth = minFinalMonth.toString();
    let minFinalDate = this.minFinalDate.getDate();
    (minFinalDate < 10) ? minFinalDate = '0' + minFinalDate : minFinalDate = minFinalDate.toString();
    this.minFinalDate = this.minFinalDate.getFullYear() + '-' + minFinalMonth + '-' + minFinalDate;

    //recalcula data maxima para tempo final para 1 ano a mais e 1 dia a menos que o dia inicial
    this.maxFinalDate = new Date(year, month - 1, day);
    this.maxFinalDate.setFullYear(this.maxFinalDate.getFullYear() + 1);
    this.maxFinalDate.setDate(this.maxFinalDate.getDate() - 1);
    let maxFinalMonth = this.maxFinalDate.getMonth() + 1;
    (maxFinalMonth < 10) ? maxFinalMonth = '0' + maxFinalMonth : maxFinalMonth = maxFinalMonth.toString();
    let maxFinalDate = this.maxFinalDate.getDate();
    (maxFinalDate < 10) ? maxFinalDate = '0' + maxFinalDate : maxFinalDate = maxFinalDate.toString();
    this.maxFinalDate = this.maxFinalDate.getFullYear() + '-' + maxFinalMonth + '-' + maxFinalDate;
  }

}
